import { gql } from "@apollo/client";
import { useQuery } from "@apollo/client/react";
import {
  Badge,
  Button,
  Card,
  Group,
  RingProgress,
  SimpleGrid,
  Stack,
  Text,
  ThemeIcon,
  Title,
} from "@mantine/core";
import {
  IconApps,
  IconChartBar,
  IconDownload,
  IconSparkles,
  IconStar,
} from "@tabler/icons-react";
import { Link } from "react-router-dom";
import { AutoDataTable } from "../../components/AutoDataTable";
import { AiDevexBanner } from "./AiDevexBanner";

// ── GraphQL Queries ──

const DEVELOPER_STATS_QUERY = gql`
  query developerStats {
    myMarketplaceApps: marketplaceApps(
      page: { first: 100 }
      filter: { ownedByMe: true }
    ) {
      nodes {
        id
        name
        icon
        status
        version
        stats {
          totalInstallations
          averageRating
          totalReviews
        }
      }
      totalCount
    }
  }
`;

const MY_PUBLISHED_APPS_QUERY = gql`
  query myPublishedApps($cursor: String, $count: Float) {
    marketplaceApps(
      page: { after: $cursor, first: $count }
      filter: { ownedByMe: true }
    ) {
      nodes {
        id
        name
        shortDescription
        icon
        status
        version
        categories
        stats {
          totalInstallations
          averageRating
          totalReviews
        }
        createdAt
      }
      totalCount
      pageInfo {
        hasNextPage
        endCursor
      }
    }
  }
`;

// ── AI DevEx Card ──

const AiDevexCard = () => (
  <Card withBorder radius="md" p="lg">
    <Stack gap="sm">
      <Group>
        <ThemeIcon size="lg" radius="md" variant="light" color="cyan">
          <IconSparkles size={20} />
        </ThemeIcon>
        <Text fw={600} size="lg">
          Build with AI
        </Text>
      </Group>
      <Text size="sm" c="dimmed">
        Use Claude Code, Cursor, ChatGPT or any MCP-capable AI to scaffold full
        WellPlayed projects — OIDC auth, CRUD hooks, webhooks, bots.
      </Text>
      <Button
        component={Link}
        to="/developer/docs#ai-development"
        color="cyan"
        variant="light"
      >
        Get started →
      </Button>
    </Stack>
  </Card>
);

// ── Developer Home ──

export function DeveloperHome() {
  const { data, loading } = useQuery<any>(DEVELOPER_STATS_QUERY);

  const apps = data?.myMarketplaceApps?.nodes ?? [];
  const totalApps = apps.length;
  const totalInstalls = apps.reduce(
    (sum: number, app: { stats?: { totalInstallations: number } }) =>
      sum + (app.stats?.totalInstallations ?? 0),
    0,
  );
  const avgRating =
    totalApps > 0
      ? apps.reduce(
          (sum: number, app: { stats?: { averageRating: number } }) =>
            sum + (app.stats?.averageRating ?? 0),
          0,
        ) / totalApps
      : 0;
  const totalReviews = apps.reduce(
    (sum: number, app: { stats?: { totalReviews: number } }) =>
      sum + (app.stats?.totalReviews ?? 0),
    0,
  );

  const publishedCount = apps.filter(
    (a: { status: string }) => a.status === "PUBLISHED",
  ).length;
  const draftCount = apps.filter(
    (a: { status: string }) => a.status === "DRAFT",
  ).length;
  const inReviewCount = apps.filter(
    (a: { status: string }) => a.status === "IN_REVIEW",
  ).length;

  return (
    <Stack gap="lg">
      <AiDevexBanner />
      <Title order={3}>Developer Dashboard</Title>
      <Text c="dimmed">
        Manage your published apps, monitor installations, and track
        performance.
      </Text>

      <AiDevexCard />

      {loading && <Text c="dimmed">Loading stats...</Text>}

      <SimpleGrid cols={{ base: 1, sm: 4, md: 4 }} spacing="lg">
        <Card withBorder padding="lg">
          <Group gap="sm" mb="xs">
            <IconApps size={20} />
            <Text size="sm" c="dimmed">
              Total Apps
            </Text>
          </Group>
          <Text size="xl" fw={700}>
            {totalApps}
          </Text>
          <Group gap={4} mt={4}>
            <Badge size="xs" color="green">
              {publishedCount} published
            </Badge>
            {draftCount > 0 && (
              <Badge size="xs" color="gray">
                {draftCount} draft
              </Badge>
            )}
            {inReviewCount > 0 && (
              <Badge size="xs" color="yellow">
                {inReviewCount} in review
              </Badge>
            )}
          </Group>
        </Card>

        <Card withBorder padding="lg">
          <Group gap="sm" mb="xs">
            <IconDownload size={20} />
            <Text size="sm" c="dimmed">
              Total Installations
            </Text>
          </Group>
          <Text size="xl" fw={700}>
            {totalInstalls}
          </Text>
        </Card>

        <Card withBorder padding="lg">
          <Group gap="sm" mb="xs">
            <IconStar size={20} />
            <Text size="sm" c="dimmed">
              Avg Rating
            </Text>
          </Group>
          <Text size="xl" fw={700}>
            {avgRating.toFixed(1)}
          </Text>
          <Text size="xs" c="dimmed">
            {totalReviews} total reviews
          </Text>
        </Card>

        <Card withBorder padding="lg">
          <Group gap="sm" mb="xs">
            <IconChartBar size={20} />
            <Text size="sm" c="dimmed">
              App Status
            </Text>
          </Group>
          <RingProgress
            size={80}
            thickness={8}
            sections={[
              {
                value: totalApps > 0 ? (publishedCount / totalApps) * 100 : 0,
                color: "green",
              },
              {
                value: totalApps > 0 ? (inReviewCount / totalApps) * 100 : 0,
                color: "yellow",
              },
              {
                value: totalApps > 0 ? (draftCount / totalApps) * 100 : 0,
                color: "gray",
              },
            ]}
          />
        </Card>
      </SimpleGrid>
    </Stack>
  );
}

// ── Developer Apps List ──

export function DeveloperApps() {
  return (
    <AutoDataTable
      query={MY_PUBLISHED_APPS_QUERY}
      createButtonText={false}
      disableNavigation
      columns={[
        {
          accessor: "name",
          title: "App Name",
        },
        {
          accessor: "version",
          title: "Version",
          rawElement: (item: any) => <Text size="sm">v{item.version}</Text>,
        },
        {
          accessor: "status",
          title: "Status",
          rawElement: (item: any) => {
            const colorMap: Record<string, string> = {
              PUBLISHED: "green",
              DRAFT: "gray",
              IN_REVIEW: "yellow",
              REJECTED: "red",
              SUSPENDED: "red",
            };
            return (
              <Badge color={colorMap[item.status] ?? "gray"} variant="light">
                {item.status}
              </Badge>
            );
          },
        },
        {
          accessor: "stats.totalInstallations",
          title: "Installs",
          rawElement: (item: any) => (
            <Text size="sm">{item.stats?.totalInstallations ?? 0}</Text>
          ),
        },
        {
          accessor: "stats.averageRating",
          title: "Rating",
          rawElement: (item: any) => (
            <Text size="sm">
              {item.stats?.averageRating?.toFixed(1) ?? "—"} (
              {item.stats?.totalReviews ?? 0})
            </Text>
          ),
        },
        {
          accessor: "createdAt",
          title: "Published",
          rawElement: (item: any) => (
            <Text size="sm">
              {new Date(item.createdAt).toLocaleDateString()}
            </Text>
          ),
        },
      ]}
    />
  );
}

// ── Developer Analytics ──

export function DeveloperAnalytics() {
  return (
    <Stack gap="lg">
      <Title order={3}>Analytics</Title>
      <Text c="dimmed">
        API usage analytics, error rates, and performance metrics for your apps.
      </Text>
      <Card withBorder padding="xl">
        <Text c="dimmed" ta="center" py="xl">
          Analytics dashboard coming soon. API usage metrics will be available
          once the monitoring infrastructure is connected.
        </Text>
      </Card>
    </Stack>
  );
}

// ── Developer Webhook Monitor ──

export function DeveloperWebhookMonitor() {
  return (
    <Stack gap="lg">
      <Title order={3}>Webhook Monitor</Title>
      <Text c="dimmed">
        Monitor webhook delivery status, view payloads, and debug issues.
      </Text>
      <Card withBorder padding="xl">
        <Text c="dimmed" ta="center" py="xl">
          Webhook monitoring coming soon. Live delivery logs will be displayed
          here once the webhook infrastructure is connected.
        </Text>
      </Card>
    </Stack>
  );
}
